import { useEffect, useState } from 'react';
import { api } from '../services/api';

interface SpreadPoint {
  time: string;
  spread_pct: number | null;
  compliant: boolean;
  bid: number | null;
  ask: number | null;
  depth_2pct: number | null;
}

interface SpreadStats {
  count: number;
  min_spread: number | null;
  max_spread: number | null;
  avg_spread: number | null;
  compliance_pct: number;
  hours: number;
}

export function SpreadHistory({ hours = 24 }: { hours?: number }) {
  const [data, setData] = useState<SpreadPoint[]>([]);
  const [stats, setStats] = useState<SpreadStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [hours]);

  const loadData = async () => {
    try {
      const resp = await api.getSpreadHistory(hours);
      setData(resp.data || []);
      setStats(resp.stats || null);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  if (loading) return <div className="spread-history loading">Loading spread history...</div>;

  const chartWidth = 800;
  const chartHeight = 180;
  const padding = { top: 20, right: 40, bottom: 30, left: 50 };
  const innerW = chartWidth - padding.left - padding.right;
  const innerH = chartHeight - padding.top - padding.bottom;

  const points = data.filter(d => d.spread_pct !== null);
  if (points.length === 0) return <div className="spread-history">No spread history data</div>;

  const spreads = points.map(p => p.spread_pct!);
  const maxSpread = Math.max(...spreads, 1.5);
  const minSpread = Math.min(...spreads, 0);

  const xScale = (i: number) => padding.left + (i / Math.max(points.length - 1, 1)) * innerW;
  const yScale = (v: number) => padding.top + innerH - ((v - minSpread) / (maxSpread - minSpread)) * innerH;

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${xScale(i).toFixed(1)},${yScale(p.spread_pct!).toFixed(1)}`).join(' ');

  // Threshold line at 1%
  const thresholdY = yScale(1.0);

  // Color segments
  const areaPath = `${linePath} L${xScale(points.length - 1).toFixed(1)},${(padding.top + innerH).toFixed(1)} L${xScale(0).toFixed(1)},${(padding.top + innerH).toFixed(1)} Z`;

  // Time labels
  const labelCount = 6;
  const labelStep = Math.max(1, Math.floor(points.length / labelCount));
  const timeLabels = points.filter((_, i) => i % labelStep === 0 || i === points.length - 1);

  return (
    <div className="spread-history">
      <div className="spread-history-header">
        <h3>Spread History ({hours}h)</h3>
        {stats && (
          <div className="spread-stats-row">
            <span className={`stat-badge ${stats.compliance_pct >= 99 ? 'good' : stats.compliance_pct >= 90 ? 'warn' : 'bad'}`}>
              {stats.compliance_pct}% compliant
            </span>
            <span className="stat-value">Avg: {stats.avg_spread?.toFixed(3)}%</span>
            <span className="stat-value">Min: {stats.min_spread?.toFixed(3)}%</span>
            <span className="stat-value">Max: {stats.max_spread?.toFixed(3)}%</span>
          </div>
        )}
      </div>
      <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="spread-chart-svg">
        {/* Area fill */}
        <path d={areaPath} fill="rgba(38,166,154,0.1)" />
        {/* 1% threshold */}
        <line
          x1={padding.left} y1={thresholdY}
          x2={chartWidth - padding.right} y2={thresholdY}
          stroke="#ef5350" strokeWidth="1" strokeDasharray="4,4" opacity="0.7"
        />
        <text x={chartWidth - padding.right + 4} y={thresholdY + 4} fill="#ef5350" fontSize="10">1%</text>
        {/* Spread line */}
        <path d={linePath} fill="none" stroke="#26a69a" strokeWidth="1.5" />
        {/* Violation dots */}
        {points.map((p, i) => p.spread_pct! > 1.0 ? (
          <circle key={i} cx={xScale(i)} cy={yScale(p.spread_pct!)} r="2.5" fill="#ef5350" />
        ) : null)}
        {/* Y axis labels */}
        {[minSpread, (minSpread + maxSpread) / 2, maxSpread].map((v, i) => (
          <text key={i} x={padding.left - 5} y={yScale(v) + 4} fill="#999" fontSize="10" textAnchor="end">
            {v.toFixed(2)}%
          </text>
        ))}
        {/* X axis time labels */}
        {timeLabels.map((p, i) => {
          const idx = points.indexOf(p);
          const t = new Date(p.time);
          return (
            <text key={i} x={xScale(idx)} y={chartHeight - 5} fill="#999" fontSize="9" textAnchor="middle">
              {t.getUTCHours().toString().padStart(2, '0')}:{t.getUTCMinutes().toString().padStart(2, '0')}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
